/**
 * What each studio action's form looks like — `.docs/20-studio-portal-pages.md`
 * §Before fan-out 2, and `.docs/00-index.md` §6's decision: "A per-action explicit
 * field map drives a generic `ActionForm`; zod introspection is used only for a
 * closed set of primitive field types. Anything else is written by hand."
 *
 * Three layers, in this order, and a field is decided by the first one that has an
 * answer for it:
 *
 *   1. **`ACTION_FIELDS`** — a name in this file. Written for a field where the shape
 *      alone gets the *label* or the *widget* wrong: `period` and `due_on` are both
 *      strings with a pattern, `amount` is an object, and "Client" is a picker rather
 *      than a slug box.
 *   2. **The published input schema.** `GET /api/studio/_catalogue` answers `inputs`,
 *      the same JSON Schema an MCP client reads (`apps/api/src/actions/registry.ts`
 *      `inputSchemas`) — so a tool that gains a field gains a form field, with the
 *      description the agent reads as its hint, and nobody has to remember.
 *   3. **`HAND_WRITTEN`** — four actions whose input is a discriminated union or an
 *      array of arrays. Each names the component that owns its form, and the generic
 *      form refuses them out loud rather than rendering half of one.
 *
 * `actionFields.test.ts` walks the recorded catalogue and fails unless every mutating
 * action lands in exactly one of the three, which is the coverage the session file
 * asks for. The introspection is deliberately narrow: it answers `null` for anything
 * it does not recognise, and `null` is a failing test rather than a text box holding
 * `[object Object]`.
 *
 * **Layer 2 is not in this file, and that is the whole of what P5 changed.** One
 * schema property becoming one form field is `components/portal/schemaFields.ts`
 * `fieldOf`, because the studio's actions are no longer the only schema in the
 * building: a widget's snapshotted `config_schema` is drawn by the client's own
 * configuration form from the same function
 * (`.plans/ai-product-placer/plan/P5-v1-1.md` §Before fan-out 3). What is left here
 * is everything that knows an action's **name** — the two maps, the layering between
 * them, and the words laid over the result.
 *
 * **A fourth layer, and it is about words rather than fields.** `actionCopy.ts` is
 * laid over whatever the three above decided: the label, the hint, the enum member
 * names and the refusal sentence. It is a *partial* override, deliberately, and its
 * own header argues why that is the opposite of the whole-replacement rule two
 * paragraphs up — a missing field is invisible, and a missing hint falls back to the
 * published description, which is always there and is only ever badly addressed.
 */

import { fieldsOf, type FieldSpec, type SchemaNode } from '../portal/schemaFields.js'
import { copyFor } from './actionCopy.js'

/* ------------------------------------------------- the four written by hand */

/**
 * The actions the generic form will not draw, and what draws them instead.
 *
 * Two shapes defeat it, and both defeat it for the same reason — a form built from a
 * shape can only ask for what the shape says, and these two say "it depends":
 *
 *   - a **discriminated union**, where choosing `kind` changes every other field;
 *   - an **array of objects that themselves hold an array**, which is a table of
 *     tables and is a component, not a field.
 *
 * A fifth candidate, `settings_set`, is *not* here: `ACTION_FIELDS` draws its `value` as
 * one `scalar` box, which writes every key but one. The tool takes any JSON since
 * session 51, and the exception — `billing.bank_details`, an object keyed by currency —
 * is written over MCP (`infra/RUNBOOK.md` §Change the bank details on an invoice).
 */
export const HAND_WRITTEN: Readonly<Record<string, string>> = {
  retainers_start:
    'PackageBuilder — items[].included_widget_qty is an array of objects inside an array ' +
    'of objects, and the price has to be computed live through catalog_compute_price as ' +
    'the blocks change.',
  retainers_change_package:
    'PackageBuilder — the same shape as retainers_start, proposed against a live retainer ' +
    'so the form shows the before and the after.',
  notifications_send:
    'The Nudge and Email-them buttons — `message` is a discriminated union over every ' +
    'template, so the fields are the chosen template’s params and a generic form would ' +
    'have to render all twenty at once.',
  reports_update_section:
    'The report editor — `section` is a discriminated union over the ten section kinds, ' +
    'each with its own tables. No page in session 20 renders it; session 21 or a later ' +
    'one owns the editor.',
  /*
   * The four config-carrying actions (session 25).
   *
   * `config` and `config_schema` are **maps**, not shapes: what is legal in one is
   * the JSON Schema a widget snapshotted from its type, which is a row in the
   * database. `fieldOf` draws a nested object only when it is `{ amount, currency }`,
   * and there is nothing generic it could draw here — the properties are not in the
   * tool definition and cannot be.
   *
   * `.plans/ai-product-placer/README.md` §6 item 12 puts the form in **v1.1**: a
   * Widgets section on the studio's client page, mounting `ActionForm` over the
   * widget's own snapshotted schema rather than over the action's. Until then these
   * four are set through `/api/studio/<name>` and over MCP, which is where a studio
   * member setting up a widget already is.
   */
  catalog_create_widget_type:
    'A v1.1 widget-type editor — `config_schema` is a JSON Schema, so its properties are ' +
    'whatever the studio is writing rather than anything a form could enumerate. Set over ' +
    'MCP or the studio RPC door until that page exists.',
  catalog_update_widget_type:
    'A v1.1 widget-type editor — the same `config_schema` map as its sibling, and the same ' +
    'answer: the shape is the document being written, not a field list.',
  widgets_create:
    'A v1.1 Widgets section on the studio client page — `config` is validated against the ' +
    'chosen type’s schema, which the form would have to fetch after the type is picked. ' +
    'The rest of the action draws fine; it is that one field that cannot.',
  /*
   * The one entry whose form now exists, and which stays here anyway.
   *
   * `pages/studio/clientDetail/WidgetsSection.tsx` mounts `ActionForm` with a
   * `schema` override — the widget's own snapshotted `config_schema`, one field per
   * property through `fieldOf`, which is why that schema is flat. The entry is about
   * the **published input**, and that has not changed: `config` is still a map whose
   * legal keys are a row in the database, and a caller with no override still cannot
   * draw it. `FieldsForOptions.override` is where the two meet.
   */
  widgets_set_config:
    'The Widgets section on the studio client page — `config` is drawn from the widget’s ' +
    'own snapshotted `config_schema`, one field per property through `fieldOf`, which is ' +
    'why that schema is flat. The action’s own input can only say "an object".',
  /*
   * Session 46. The blog workspace is the form, mounted in the studio over the
   * `blog_*` actions (`pages/blog/`, decision 12).
   */
  blog_update_post:
    'The blog workspace’s editor (pages/blog/PostEditorPage.tsx) — `body` is a document, ' +
    'an open map the Editor primitive writes, and a save is the whole working copy sent ' +
    'from the draft_version it was made from, by an autosave rather than a submit.',
  /*
   * Session 48b. A mockup version can travel inside the call — `html`, or `files` as
   * text and base64 — which is a chat client's door, not a person's: nobody pastes a
   * PNG's base64 into a box. The person's form was always the upload button's file
   * picker, which sends the bytes to `mockups_create_upload`'s URL instead.
   */
  mockups_create:
    'The mockup pages’ upload button (pages/mockups/UploadMockupButton.tsx) — a title and ' +
    'a file, sent as an upload; `html` and `files` carry a version inside the call, for ' +
    'a caller that cannot make an upload of its own.',
  mockups_add_version:
    'The mockup pages’ upload button (pages/mockups/UploadMockupButton.tsx) — a person ' +
    'sends the next version as a file; this tool’s `files` are text and base64 inside ' +
    'the call, which is a chat client’s way and no form’s.',
}

/* ------------------------------------------------------- the explicit entries */

/**
 * Fields the shape alone gets wrong, by action.
 *
 * An entry here **replaces** introspection for that action, whole — a partial
 * override would be a third place a field could come from, and the reason a field is
 * missing from a form is exactly the kind of thing nobody notices. Where an entry
 * exists it lists every field the action takes except the ones a program fills in
 * (`MACHINE_FIELDS`).
 *
 * `BY_NAME` in `components/portal/schemaFields.ts` is the reason the list is short: a
 * field called `client` is a client picker in all forty-two actions that take one,
 * and a field called `period` is a month picker in all nineteen, so those are rules
 * rather than rows.
 */
export const ACTION_FIELDS: Readonly<Record<string, readonly FieldSpec[]>> = {
  /*
   * `settings_set` — the one action whose value has no single type.
   *
   * `SETTING_KEYS` is the API's own union and the SPA has no copy of it, so `key` is
   * a text field with the hint the schema wrote rather than a select over a list
   * this side would have to keep in step.
   */
  settings_set: [
    { name: 'key', widget: 'text', label: 'Setting', optional: false },
    {
      name: 'value',
      widget: 'scalar',
      label: 'Value',
      hint: 'true, false, a number, or text. What the key expects is in its own description.',
      optional: false,
    },
  ],

  /*
   * `tasks_assign` — the one action whose **two** fields are one decision.
   *
   * R3-04: the form offered an `Assignee kind` select and a plain text box whose help
   * text was the tool's own parameter documentation ("The person's user id or the
   * agent's id. Null, and only null, for 'none'."). Typing a colleague's name gave
   * `Invalid UUID`, and there was no list of the four studio people anywhere on the
   * screen. Assignment was unusable without a database.
   *
   * `assignee_kind` and `assignee_id` are not two questions — the API's own
   * `ValidationFailed` says so ("An assignee is a person or an agent with an id, or
   * nobody at all"), and the pair is exactly what `tasks_list_assignees` answers. So
   * one control answers both, and `ActionForm` `bodyOf` is where that pair is split
   * apart again. It is the only widget that writes two of an action's fields, and it
   * is here rather than as a rule because it is one action rather than a shape.
   */
  tasks_assign: [
    { name: 'task_ref', widget: 'ref', label: 'Task', optional: false },
    { name: 'assignee', widget: 'assignee', label: 'Who it goes to', optional: false },
  ],
}

/* ---------------------------------------------------------- the introspection */

export type FieldsForOptions = {
  /**
   * True when `schema` is the **caller's**, not the action's published input.
   *
   * `ActionForm`'s `schema` prop — one action uses it, `widgets_set_config`, over the
   * widget's own snapshotted `config_schema` (`.plans/ai-product-placer/plan/
   * P5-v1-1.md` item (d3)). Two things change, and both follow from what an override
   * *is*.
   *
   * **`HAND_WRITTEN` yields to it**, and the alternative — taking `widgets_set_config`
   * off the list — was the wrong answer for two reasons. The entry is not a statement
   * about the action, it is a statement about the **published input**: `config` is a
   * map whose legal keys are a row in the database, so "the action's own input can
   * only say 'an object'" is exactly as true after this session as before it, and
   * `actionFields.test.ts`'s coverage walk — which reads `catalogue.inputs` and knows
   * nothing about any override — would go from passing to failing on an action whose
   * form now exists. And the entry is the sentence that tells the next reader where
   * that form is; deleting it deletes the answer along with the question. So the
   * pinned list of eight stays eight, and the hand-written check is skipped for the
   * one caller that has brought the schema the entry says it could not have.
   *
   * **`actionCopy.ts` does not apply.** A key there may be a bare field name —
   * `client` is written once for the forty-two actions that take one — so words
   * written for a published field would relabel a config property that happened to
   * share its name, in a form whose labels are the schema's own mandatory `title`s
   * (`packages/shared/src/schemas/widgetConfig.ts`: "it is the label the client
   * reads"). An override brings its own words with it.
   */
  readonly override?: boolean
}

/**
 * The fields of one action's form, or null when nothing here can draw it.
 *
 * `null` is the answer for an action on `HAND_WRITTEN` and for one whose schema holds
 * a shape `fieldOf` does not recognise. `ActionForm` renders the reason; the
 * coverage test fails on the second case and passes on the first.
 *
 * The walk itself is `components/portal/schemaFields.ts` `fieldsOf` — properties,
 * `required`, and one field each — because the client's configuration form makes the
 * same walk over a schema that is a database row. What is left here is layer 1,
 * layer 3 and the words.
 */
export function fieldsFor(
  action: string,
  schema: SchemaNode | undefined,
  options: FieldsForOptions = {},
): FieldSpec[] | null {
  if (options.override === true) {
    return schema === undefined ? null : fieldsOf(schema)
  }
  if (action in HAND_WRITTEN) return null
  const explicit = ACTION_FIELDS[action]
  // An explicit entry decides the **fields**; the words layer still applies to each
  // of them, so a label written once for `client` is not written again per action.
  if (explicit !== undefined) return explicit.map((field) => worded(action, field))
  if (schema === undefined) return null

  // `defineAction` adds the idempotency key to every mutating action and
  // `useStudioAction` generates it. A form that asked for it would be asking the
  // reader to decide whether their own retry is the same call. The stamp beside it is
  // the same kind of field for another reason, and `MACHINE_FIELDS` gives each.
  const fields = fieldsOf(schema, { skip: MACHINE_FIELDS })
  if (fields === null) return null
  const wordedFields = fields.map((field) => worded(action, field))
  /*
   * `tasks_create` may name an assignee at raise (#41). The published pair is the
   * same two fields as `tasks_assign`; one picker answers both, and `bodyOf` splits
   * them. Drop the introspected pair when present (after a catalogue refresh) so the
   * form never shows a uuid box beside a kind select.
   */
  if (action === 'tasks_create') {
    return orderForCreateDialog([
      ...wordedFields.filter(
        (field) => field.name !== 'assignee_kind' && field.name !== 'assignee_id',
      ),
      worded(action, {
        name: 'assignee',
        widget: 'assignee',
        label: 'Who it goes to',
        optional: true,
      }),
    ])
  }
  return wordedFields
}

/**
 * Row pairing the wide New-task dialog draws (#39): Client+Queue, What to do +
 * Done when, Priority+Due, Money+Service — each pair on one row of the
 * two-column grid, with Title and References the only full-width rows.
 *
 * `tasks_create`'s own schema order (client, title, instructions, acceptance,
 * queue, priority, …) does not group into those pairs — a plain 2-up grid over
 * it pairs Queue with Priority and Due with Needs-review instead, and leaves
 * Client on a row by itself. The draw order is named once, here, rather than
 * reordering the API's input schema for a fact that is this dialog's alone.
 */
const CREATE_DIALOG_ORDER: readonly string[] = [
  'title',
  'client',
  'queue',
  'instructions',
  'acceptance',
  'priority',
  'due_at',
  'money',
  'service_code',
  'needs_review',
  'money_ref',
  'context_refs',
  'assignee',
]

function orderForCreateDialog(fields: readonly FieldSpec[]): FieldSpec[] {
  const byName = new Map(fields.map((field) => [field.name, field]))
  const ordered: FieldSpec[] = []
  for (const name of CREATE_DIALOG_ORDER) {
    const field = byName.get(name)
    if (field === undefined) continue
    ordered.push(field)
    byName.delete(name)
  }
  return [...ordered, ...byName.values()]
}

/** The field `apps/api/src/actions/define.ts` adds. `api/studio.ts` states it too. */
const IDEMPOTENCY_FIELD = 'idempotency_key'

/**
 * "Only if it is still what I read" — the `updated_at` of the copy a change was made
 * from, which `tasks_update`, `tasks_assign` and `tasks_release` take so that a change
 * made from an old copy cannot overwrite a newer one.
 *
 * It is never drawn, and not because it is awkward to fill in: it is a fact about
 * what the **page** last read, and only the page has it. Published as an RFC 3339
 * instant, the shape alone would draw it as a day-and-time box labelled "If unchanged
 * since" under a description written for a tool caller — and a reader who typed a
 * time into it would be choosing, by hand, whether their own save is refused. The
 * code that has a stamp to send is the code that sends it: the Tasks page's Undo
 * (`pages/studio/tasks/fieldSave.ts`).
 */
export const UNCHANGED_SINCE_FIELD = 'if_unchanged_since'

/**
 * A spot on a mockup's page (session 49), which a gesture in the frame makes — the review's
 * comment box sends it (`pages/mockups/LinkViewerPage.tsx`), and nobody types a CSS selector.
 */
export const PIN_FIELD = 'pin'

/**
 * The thread a reply goes into (session 55) — the review's reply box sends it, from the thread
 * it is in; nobody types a comment's id.
 */
export const REPLY_TO_FIELD = 'reply_to'

/**
 * The inputs a **program** fills in, which no form asks a person for.
 *
 * Skipped by name wherever the published schema is walked, so the day a fourth tool
 * takes the stamp its form does not grow the box. An `ACTION_FIELDS` entry lists its
 * own fields and names neither, and an `only=` that named one would draw nothing —
 * the field is not among the ones there are to choose from.
 */
export const MACHINE_FIELDS: readonly string[] = [
  IDEMPOTENCY_FIELD,
  UNCHANGED_SINCE_FIELD,
  'if_was',
  PIN_FIELD,
  REPLY_TO_FIELD,
]

/**
 * "Only if this is still what it says" — what each field being changed held when it
 * was read, which `tasks_update` takes so that an edit made from an old copy is
 * refused rather than written over somebody else's; and who held the task and where
 * it stood, which `tasks_assign` and the seven moves a person makes take for the same
 * reason.
 *
 * Never drawn, for `UNCHANGED_SINCE_FIELD`'s reason: it is a fact about what the
 * page showed, and the code that has it is the code that sends it — the form that
 * drew the boxes for an edit (`ActionForm`, `components/portal/schemaForm.ts`
 * `changesOf`), the page for a move or a hand-over (`taskWords.ts` `moveOf`).
 */
export const WAS_FIELD = 'if_was'

/**
 * The fields an action lets a caller say "it was this" about, read off its published
 * input — the names under `if_was` — or none, for an action that takes no such thing.
 *
 * Off the schema rather than a list of actions here, so the day a third tool takes
 * the condition its forms send it by saying nothing.
 */
export function wasFieldsOf(schema: SchemaNode | undefined): ReadonlySet<string> {
  const properties = (schema as { properties?: Record<string, unknown> } | undefined)?.properties
  const was = properties?.[WAS_FIELD] as { properties?: Record<string, unknown> } | undefined
  return new Set(Object.keys(was?.properties ?? {}))
}

/**
 * One field, with the words a person reads laid over the ones a tool caller does.
 *
 * `actionCopy.ts` is the whole argument; the mechanics are a dozen lines. The label
 * and the hint are overridden where there is copy for them, and an `options` entry
 * **relabels the members it names and leaves the rest alone**, so a queue that gains
 * an eighth value still renders — as `labelOf` spells it — rather than disappearing
 * from the list.
 *
 * Two things worth stating out loud:
 *
 *   - **`hint: null` is deliberate silence.** `undefined` means "nobody has written
 *     one, keep the published description"; `null` means "the label is the whole of
 *     it". A field like *Which attempt* under a box that already says which attempt
 *     it is does not need a muted line restating the label, and padding one out is
 *     how a form ends up unreadable — CONTRACT.md §2.8 gives a field **one** muted
 *     line and this is the decision not to spend it.
 *   - **A row's fields get the same treatment.** `rowFieldsOf` builds them out of the
 *     schema and knows nothing about the action, so a `catalog_create_package.items`
 *     row printed `catalog_list_services lists them` under its Service box while the
 *     identical field one level up said "off the rate card". The recursion is here
 *     rather than there because this is the only function that has the action's name,
 *     and a nested field is keyed by its own name — `catalog_create_package.service`,
 *     not `…items.service` — because a row is a shape, not a namespace.
 */
function worded(action: string, field: FieldSpec): FieldSpec {
  const copy = copyFor(action, field.name)
  const options =
    copy.options === undefined || field.options === undefined
      ? field.options
      : field.options.map((one) => ({
          value: one.value,
          label: copy.options?.[one.value] ?? one.label,
        }))
  const hint = copy.hint === undefined ? field.hint : (copy.hint ?? undefined)
  const fields = field.fields?.map((inner) => worded(action, inner))

  // The published hint is dropped from the spread rather than overwritten, so that
  // `hint: null` can leave the field with no muted line at all.
  const { hint: _published, ...rest } = field
  return {
    ...rest,
    label: copy.label ?? field.label,
    ...(hint === undefined ? {} : { hint }),
    ...(options === undefined ? {} : { options }),
    ...(fields === undefined ? {} : { fields }),
    ...(copy.required === undefined ? {} : { required: copy.required }),
    ...(copy.add === undefined ? {} : { addLabel: copy.add }),
  }
}
